/**
 * The icon set. Inline stroked SVG on one 24×24 grid, one stroke weight.
 *
 * WHY NOT EMOJI, which is what this app shipped first: an emoji is a font
 * glyph the platform chooses, so 📷 is a flat grey rectangle on Windows, a
 * glossy skeuomorphic camera on macOS, and a third thing on Android. The booth
 * laptop and the visitor's phone are guaranteed to be different platforms —
 * that is the entire demo — so emoji guarantees the two screens do not match.
 * They also render at whatever weight and colour the vendor chose, next to type
 * we control precisely, which is exactly what "looks cheaply made" is made of.
 *
 * Drawn rather than imported: eleven icons is not worth a dependency, and an
 * icon package would bring hundreds we never render plus a licence to track.
 *
 * ACCESSIBILITY: every icon here is DECORATIVE and marked aria-hidden. There is
 * no variant that carries meaning alone — each one sits beside real text that
 * says the same thing. If you ever need an icon to be the only label, give the
 * button an aria-label; do not add a title to the SVG.
 */

export type IconName =
  | "camera"
  | "sparkle"
  | "card"
  | "arrow-up"
  | "arrow-down"
  | "check"
  | "backspace"
  | "reset"
  | "wifi-off"
  | "grid"
  | "dollar"
  | "chart"
  | "download"
  | "gear";

interface IconProps {
  name: IconName;
  /** Rendered size in px; the icon is square. */
  size?: number;
  className?: string;
  /** Stroke weight. The default suits 16–24px; go thinner only when larger. */
  strokeWidth?: number;
}

/**
 * Paths only — every icon shares the same wrapper below, which is what keeps the
 * set looking like a set: identical viewBox, cap, join, and stroke weight.
 */
const PATHS: Record<IconName, JSX.Element> = {
  camera: (
    <>
      <path d="M3 8.5A1.5 1.5 0 0 1 4.5 7h2.2l1-1.6A1 1 0 0 1 8.6 5h6.8a1 1 0 0 1 .9.4l1 1.6h2.2A1.5 1.5 0 0 1 21 8.5v9A1.5 1.5 0 0 1 19.5 19h-15A1.5 1.5 0 0 1 3 17.5z" />
      <circle cx="12" cy="12.6" r="3.4" />
    </>
  ),
  sparkle: (
    <>
      <path d="M12 3.5l1.9 4.9 4.9 1.9-4.9 1.9L12 17.1l-1.9-4.9-4.9-1.9 4.9-1.9z" />
      <path d="M18.5 15.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" />
    </>
  ),
  card: (
    <>
      <rect x="2.6" y="5.4" width="18.8" height="13.2" rx="2.4" />
      <path d="M2.6 9.9h18.8" />
    </>
  ),
  "arrow-up": (
    <>
      <path d="M12 19V5" />
      <path d="M6 11l6-6 6 6" />
    </>
  ),
  "arrow-down": (
    <>
      <path d="M12 5v14" />
      <path d="M6 13l6 6 6-6" />
    </>
  ),
  check: <path d="M4.5 12.5l5 5 10-11" />,
  backspace: (
    <>
      <path d="M21 5.5H9.6a2 2 0 0 0-1.5.7l-4.4 5a1.2 1.2 0 0 0 0 1.6l4.4 5a2 2 0 0 0 1.5.7H21a1.5 1.5 0 0 0 1.5-1.5v-10A1.5 1.5 0 0 0 21 5.5z" />
      <path d="M12.5 9.8l5 4.4M17.5 9.8l-5 4.4" />
    </>
  ),
  reset: (
    <>
      <path d="M20 12a8 8 0 1 1-2.6-5.9" />
      <path d="M20.4 4.2v4.4H16" />
    </>
  ),
  "wifi-off": (
    <>
      <path d="M3 4l18 16" />
      <path d="M5.2 9.6a13 13 0 0 1 3.4-2.1M19.4 9.9a13 13 0 0 0-5.6-2.7" />
      <path d="M8.2 13.4a8 8 0 0 1 2-1.2M16.6 13.9a8 8 0 0 0-1.6-1" />
      <path d="M11.2 17.2a3.2 3.2 0 0 1 2.6.4" />
    </>
  ),
  // Four quadrants — the dashboard's own nav glyph. Reads as "overview" at 18px,
  // the size every nav link in the app shell renders icons at.
  grid: (
    <>
      <rect x="3" y="3" width="7.5" height="7.5" rx="1.4" />
      <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.4" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.4" />
      <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.4" />
    </>
  ),
  // A single bill with a coin — payroll is money moving OUT, so this is
  // deliberately not the same glyph as "card" (money moving as a purchase).
  dollar: (
    <>
      <rect x="2.5" y="6" width="19" height="12" rx="2" />
      <circle cx="12" cy="12" r="3.1" />
      <path d="M5.5 9v6M18.5 9v6" />
    </>
  ),
  // Three bars, rising left to right — the same shape CashFlowChart draws, so
  // the nav glyph previews what the screen behind it looks like.
  chart: (
    <>
      <path d="M4 20V10" />
      <path d="M12 20V4" />
      <path d="M20 20v-7" />
    </>
  ),
  /* Settings & admin. A hub, so the icon has to read as a CATEGORY rather than as
     any one thing inside it — the gear is the one shape everybody already knows
     means "the settings area", which is exactly what this nav item is. */
  gear: (
    <>
      <circle cx="12" cy="12" r="3.1" />
      {/* A toothed RING, not a circle with rays. The first attempt was a circle plus eight
          radiating strokes, which at the 20px the nav renders it at reads as a sun or a
          brightness control — the teeth have to be part of the outline for the shape to
          say "gear" at that size. */}
      <path d="M19.5 14.6a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.03 1.55V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.56 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.7 1.7 0 0 0 .34-1.87 1.7 1.7 0 0 0-1.55-1.03H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.56-1.1 1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.87.34H9a1.7 1.7 0 0 0 1.03-1.55V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1.03 1.55 1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.87V9a1.7 1.7 0 0 0 1.55 1.03H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.4 1.03z" />
    </>
  ),
  // Tray + downward arrow — a file landing, for the FINPACK export control.
  download: (
    <>
      <path d="M12 4v11" />
      <path d="M7.5 11.5L12 16l4.5-4.5" />
      <path d="M4.5 17v2A1.5 1.5 0 0 0 6 20.5h12a1.5 1.5 0 0 0 1.5-1.5v-2" />
    </>
  ),
};

export function Icon({ name, size = 18, className, strokeWidth = 1.7 }: IconProps) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      /* Inherits the text colour of whatever it sits in, so an icon never needs
         its own colour rule and can never fall out of sync with its label. */
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
